"use client";

import {createContext, use} from "react";

import type {TActiveSessionContext} from "@/providers/activeSession.provider";
import type {TAlertContext} from "@/providers/alert.provider";
import type {JamDeckContextType} from "@/providers/jamDeck.provider";
import type * as Y from "yjs";
import type {CollaborationContextType} from "./collaboration.provider";
import type {TGameDeckContext} from "./gameDeck.provider";
import type {TJamDeckCollabContext} from "./jamDeckCollab.provider";
import type {TPlayerSetupContext} from "./playerSetup.provider";
import type {TPlaylistsContext} from "./playlists.provider";
import type {TPlayerContext} from "./preGame.provider";
import type {TSongContext} from "./song.provider";
import type {TSoundEffectContext} from "./soundEffect.provider";
import type {TUpsertPlaylistContext} from "./upsertPlaylist.provider";
import type {TUserMgmtContext} from "./userMgmt.provider";
import type {UserProfileContextType} from "./userProfile.provider";

export interface DocumentContextType {
    document: Y.Doc;
}

export const PreGameContext = createContext<TPlayerContext | null>(null);
export const usePreGameContext = () => {
    const values = use(PreGameContext);
    if (!values) throw new Error("Cannot use PreGameContext outside of provider");

    return values;
};

export const JamDeckContext = createContext<JamDeckContextType | null>(null);
export const useJamDeckContext = () => {
    const values = use(JamDeckContext);
    if (!values) {
        throw new Error("useJamDeckContext must be used within a JamDeckProvider");
    }

    return values;
};

export const SoundEffectContext = createContext<TSoundEffectContext | null>(null);
export const useSoundEffectContext = () => {
    const values = use(SoundEffectContext);
    if (!values) throw new Error("Cannot use SoundEffectContext outside of provider");

    return values;
};

export const SongContext = createContext<TSongContext | null>(null);
export const useSongContext = () => {
    const values = use(SongContext);
    if (!values) throw new Error("Cannot use SongContext outside of provider");

    return values;
};

export const GameDeckContext = createContext<TGameDeckContext | null>(null);
export const useGameDeckContext = () => {
    const values = use(GameDeckContext);
    if (!values) throw new Error("Cannot use GameDeckContext outside of provider");

    return values;
};

export const CollaborationContext = createContext<CollaborationContextType | null>(null);
export const useCollaborationContext = () => {
    const values = use(CollaborationContext);
    if (!values) throw new Error("Cannot use CollaborationContext outside of provider");

    return values;
};

export const DocumentContext = createContext<DocumentContextType | null>(null);
export const useDocumentContext = () => {
    const values = use(DocumentContext);
    if (!values) throw new Error("Cannot use DocumentContext outside of provider");

    return values;
};

export const PlayerSetupContext = createContext<TPlayerSetupContext | null>(null);
export const usePlayerSetupContext = () => {
    const values = use(PlayerSetupContext);
    if (!values) throw new Error("Cannot use PlayerSetupContext outside of provider");

    return values;
};

export const JamDeckCollabContext = createContext<TJamDeckCollabContext | null>(null);
export const useJamDeckCollabContext = () => {
    const values = use(JamDeckCollabContext);
    if (!values) throw new Error("Cannot use JamDeckCollabContext outside of provider");

    return values;
};

export const UserProfileContext = createContext<UserProfileContextType | null>(null);
export const useUserProfileContext = () => {
    const values = use(UserProfileContext);
    if (!values) throw new Error("Cannot use UserProfileContext outside of provider");

    return values;
};

export const AlertContext = createContext<TAlertContext | null>(null);
export const useAlertContext = () => {
    const context = use(AlertContext);
    if (!context) {
        throw new Error("useAlert must be used within an AlertProvider");
    }

    return context;
};

export const UpsertPlaylistContext = createContext<TUpsertPlaylistContext | null>(null);
export const useUpsertPlaylistContext = () => {
    const values = use(UpsertPlaylistContext);
    if (!values) throw new Error("Cannot use UpsertPlaylistContext outside of provider");

    return values;
};

export const PlaylistsContext = createContext<TPlaylistsContext | null>(null);
export const usePlaylistsContext = () => {
    const values = use(PlaylistsContext);
    if (!values) throw new Error("Cannot use PlaylistsContext outside of provider");

    return values;
};

export const UserMgmtContext = createContext<TUserMgmtContext | null>(null);
export const useUserMgmtContext = () => {
    const values = use(UserMgmtContext);
    if (!values) throw new Error("Cannot use UserMgmtContext outside of provider");

    return values;
};

export const ActiveSessionContext = createContext<TActiveSessionContext | null>(null);
export const useActiveSessionContext = () => {
    const values = use(ActiveSessionContext);
    if (!values) throw new Error("Cannot use ActiveSessionContext outside of provider");

    return values;
};
